import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import useListLayout from "./useListLayout";

const SEARCH_DEBOUNCE_MS = 300;

/** Case-insensitive (by column collation) match on name OR description. */
export const nameDescriptionSearch = (text) => {
    const q = text?.trim();
    if (!q) return null;
    return {
        OR: [{ name: { contains: q } }, { description: { contains: q } }],
    };
};

/**
 * Table sort ({ field, direction } from DataTable's onSort) -> Prisma orderBy.
 * Nullable columns take { sort } rather than a bare direction in the generated
 * *OrderByWithRelationInput, so list them in `nullableFields`.
 */
export const sortToOrderBy = (sort, nullableFields = []) => {
    if (!sort?.field) return [];
    const direction = sort.direction === "desc" ? "desc" : "asc";
    return [
        {
            [sort.field]: nullableFields.includes(sort.field)
                ? { sort: direction }
                : direction,
        },
    ];
};

/**
 * Server-side pagination for an entity list.
 *
 * One request returns the current page and the total count:
 *
 *   query GetParts($where: PartWhereInput!, $orderBy: [PartOrderByWithRelationInput!],
 *                  $take: Int, $skip: Int) {
 *       parts(where: $where, orderBy: $orderBy, take: $take, skip: $skip) { ... }
 *       aggregatePart(where: $where) { _count { _all } }
 *   }
 *
 * The page lives in the URL (?page=N) like the rest of the list state, so a refresh or
 * deep link restores it. The page size is the user's list layout from the Settings
 * page (useListLayout, 20 by default, at most 50), and the first request waits for
 * it. Search runs on the server (debounced) because a page only holds one page of rows. Changing the filter (where / search / sort) returns to page 1, and a
 * page left empty by a delete steps back to the last page that still has rows.
 *
 * @param {object}   opts
 * @param {DocumentNode} opts.query     list + aggregate query shaped as above
 * @param {string}   opts.listField     root list field, e.g. "parts"
 * @param {string}   opts.countField    aggregate field, e.g. "aggregatePart"
 * @param {object}   opts.where         base filter (plant, is_active, ...)
 * @param {string}   opts.searchText    raw search box text
 * @param {Function} opts.buildSearch   text -> where fragment (default: name/description)
 * @param {Array}    opts.orderBy       Prisma orderBy list; an id tie-break is appended
 * @param {object}   opts.client        Apollo client (engineClient)
 * @param {boolean}  opts.skip          skip the query (e.g. no plant yet)
 */
export default function usePagedList({
    query,
    listField,
    countField,
    where,
    searchText = "",
    buildSearch = nameDescriptionSearch,
    orderBy = [],
    client,
    skip = false,
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const layout = useListLayout();
    const pageSize = layout.pageSize;
    const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

    const setPage = (next, options = { replace: true }) =>
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (next > 1) params.set("page", String(next));
            else params.delete("page");
            return params;
        }, options);

    // Debounce the search box so typing does not fire a request per keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState(searchText);
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchText]);

    const fullWhere = useMemo(() => {
        const search = buildSearch(debouncedSearch);
        return search ? { AND: [where, search] } : where;
    }, [where, debouncedSearch, buildSearch]);

    // A stable id tie-break keeps rows from shuffling between pages on equal sort keys.
    const fullOrderBy = useMemo(
        () => (orderBy.some((o) => "id" in o) ? orderBy : [...orderBy, { id: "asc" }]),
        [orderBy],
    );

    // Back to page 1 whenever what is being paged changes (not on the first render,
    // so a deep link to ?page=3 is honoured).
    const filterKey = JSON.stringify([fullWhere, fullOrderBy]);
    const lastFilterKey = useRef(filterKey);
    useEffect(() => {
        if (lastFilterKey.current === filterKey) return;
        lastFilterKey.current = filterKey;
        if (page !== 1) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey]);

    const { data, previousData, loading, error, refetch } = useQuery(query, {
        client,
        skip: skip || !layout.ready,
        variables: {
            where: fullWhere,
            orderBy: fullOrderBy,
            take: pageSize,
            skip: (page - 1) * pageSize,
        },
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
    });

    // Keep showing the previous page while the next one loads, instead of a blank list.
    const shown = data ?? previousData;
    const items = useMemo(() => shown?.[listField] ?? [], [shown, listField]);
    const total = shown?.[countField]?._count?._all ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // A delete can leave the current page empty; step back to the last real page.
    useEffect(() => {
        if (loading || !data) return;
        if (page > pageCount) setPage(pageCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data, page, pageCount]);

    return {
        items,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading,
        initialLoading: (!skip && !layout.ready) || (loading && !data && !previousData),
        error,
        refetch,
        isSearching: !!debouncedSearch?.trim(),
    };
}
